import { useCallback, useEffect, useState } from "react";

import { useParams } from "@tanstack/react-router";

import {
  SignClientTypes,
  handleRedirectAndClose,
} from "@reown/web-wallet-core";

import { AppLogo } from "../components/ui/AppLogo";
import { Container } from "../components/ui/Container";
import Spacer from "../components/ui/Spacer";
import { useWallet } from "../hooks/useWallet";

export function EmailUpdate() {
  const { wallet, isReady, error: walletError, isInitializing } = useWallet();
  const { sessionTopic } = useParams({ from: "/emailUpdate/$sessionTopic" });
  const [peerMetadata, setPeerMetadata] = useState<
    SignClientTypes.Metadata | undefined
  >();
  const [status, setStatus] = useState<
    "checking" | "processing" | "success" | "error"
  >("checking");
  const [error, setError] = useState<string>();
  const [updatedEmail, setUpdatedEmail] = useState<string>();

  useEffect(() => {
    if (walletError) {
      setError(walletError);
      setStatus("error");
    }
  }, [walletError]);

  const updateEmail = useCallback(async () => {
    if (!wallet || !isReady || !sessionTopic) return;

    try {
      const session = wallet?.walletKit?.getActiveSessions()[sessionTopic];
      if (!session) {
        setError("Session not found");
        setStatus("error");
        return;
      }

      setPeerMetadata(session.peer.metadata);

      const result = await wallet.openUpdateEmailModal(sessionTopic);
      setUpdatedEmail(result?.email);
      setStatus("success");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update email");
      setStatus("error");
    }
  }, [wallet, isReady, sessionTopic]);

  useEffect(() => {
    if (wallet && isReady && sessionTopic && status === "checking") {
      updateEmail();
    }
  }, [wallet, isReady, sessionTopic, status, updateEmail]);

  const handleRedirect = () => {
    handleRedirectAndClose(peerMetadata, wallet?.walletKit);
  };

  const ErrorContent = () => (
    <>
      <AppLogo logoUrl={peerMetadata?.icons?.[0]} status="error" />
      <Spacer size="2" />
      <wui-text variant="lg-medium" color="primary" align="center">
        {error || "Connection failed"}
      </wui-text>
      <Spacer size="2" />
      <wui-button
        size="lg"
        fullWidth
        variant="neutral-secondary"
        onClick={handleRedirect}
      >
        Back to {peerMetadata?.name}
        <wui-icon
          size="sm"
          color="inherit"
          slot="iconRight"
          name="externalLink"
        />
      </wui-button>
    </>
  );

  const SuccessContent = () => (
    <>
      <AppLogo logoUrl={peerMetadata?.icons?.[0]} status="success" />
      <Spacer size="2" />
      <wui-text variant="lg-medium" color="primary" align="center">
        Email Updated!
      </wui-text>
      <Spacer size="2" />
      <wui-button
        size="lg"
        fullWidth
        variant="neutral-secondary"
        onClick={handleRedirect}
      >
        Back to {peerMetadata?.name}
        <wui-icon
          size="sm"
          color="inherit"
          slot="iconRight"
          name="externalLink"
        />
      </wui-button>
    </>
  );

  const LoadingContent = () => (
    <>
      <AppLogo />
      <Spacer size="2" />
      <wui-text variant="lg-medium" color="primary" align="center">
        {status === "checking" || isInitializing
          ? "Preparing email update..."
          : "Updating email..."}
      </wui-text>
      <Spacer size="2" />
    </>
  );

  const SuccessFooter = () => (
    <wui-text variant="md-regular" color="secondary" align="center">
      Your email address has been changed to {updatedEmail}.
      <br />
      You can now return to the app.
    </wui-text>
  );

  const renderContent = () => {
    if (status === "error") {
      return <ErrorContent />;
    }

    if (status === "success") {
      return <SuccessContent />;
    }

    return <LoadingContent />;
  };

  const renderFooter = () => {
    if (status === "success") {
      return <SuccessFooter />;
    }

    return null;
  };

  return (
    <>
      <Container>{renderContent()}</Container>
      <Spacer size="2" />
      {renderFooter()}
    </>
  );
}
